import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { Eye, EyeOff } from "lucide-react";
import { resetPassword } from "../../api/auth_api";

/* SAME PASSWORD RULES AS SIGNUP */
const passwordRules = {
    length: (v) => v.length >= 8,
    upper: (v) => /[A-Z]/.test(v),
    lower: (v) => /[a-z]/.test(v),
    number: (v) => /\d/.test(v),
    special: (v) => /[@$!%*?&#]/.test(v),
};

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const [formData, setFormData] = useState({
        new_password: "",
        confirm_password: "",
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [passwordFocused, setPasswordFocused] = useState(false);
    const [confirmFocused, setConfirmFocused] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleChange = (e) => {
        setFormData((p) => ({
            ...p,
            [e.target.name]: e.target.value,
        }));
    };

    /* PASSWORD CHECKS */
    const passwordChecks = {
        length: passwordRules.length(formData.new_password),
        upper: passwordRules.upper(formData.new_password),
        lower: passwordRules.lower(formData.new_password),
        number: passwordRules.number(formData.new_password),
        special: passwordRules.special(formData.new_password),
    };

    const isPasswordValid = Object.values(passwordChecks).every(Boolean);
    const isPasswordMatch =
        formData.new_password &&
        formData.new_password === formData.confirm_password;

    const handleSubmit = async (e) => {
        e.preventDefault();

        // if (!token) {
        //     setError("Invalid or expired reset link.");
        //     return;
        // }

        if (!isPasswordValid || !isPasswordMatch) return;

        setLoading(true);
        setError(null);

        const res = await resetPassword({
            reset_token: token,
            new_password: formData.new_password,
        });

        if (res.success) {
            setSuccess(true);
            setTimeout(() => navigate("/login"), 2500);
        } else {
            setError(res.error || "Password reset failed.");
        }

        setLoading(false);
    };

    const Rule = ({ ok, label }) => (
        <div className={`text-sm ${ok ? "text-green-400" : "text-slate-400"}`}>
            {ok ? "✔" : "•"} {label}
        </div>
    );

    return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-[#0F172A]">
            <div
                className="w-full max-w-md bg-[#1E293B]
                       border border-slate-700
                       rounded-xl shadow-lg
                       p-6 sm:p-8"
            >
                {/* Logo */}
                <h1
                    className="font-semibold mb-4 sm:mb-6 text-center text-2xl sm:text-3xl text-blue-400"
                >
                    FactorAlpha
                </h1>

                {!success ? (
                    <>
                        <div className="mb-5 sm:mb-6 text-center">
                            <p className="font-semibold text-lg sm:text-xl mb-1 text-white">
                                Reset Your Password
                            </p>
                            <p className="text-xs sm:text-sm text-slate-400">
                                Enter a new password for your account
                            </p>
                        </div>

                        {error && (
                            <div className="bg-red-500/20 text-red-400 p-3 rounded-lg text-sm mb-4 border border-red-500/50">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* NEW PASSWORD */}
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="new_password"
                                    placeholder="New Password"
                                    value={formData.new_password}
                                    onChange={handleChange}
                                    onFocus={() => setPasswordFocused(true)}
                                    required
                                    className="w-full px-4 py-3 sm:py-3.5 bg-slate-800/50 border border-slate-700 rounded-lg pr-12 text-sm sm:text-base text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-slate-200 transition-colors"
                                >
                                    {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                                </button>
                            </div>

                            {/* PASSWORD RULES (HIDE WHEN VALID ✅) */}
                            {passwordFocused && !isPasswordValid && (
                                <div className="grid grid-cols-1 gap-1 pl-1 text-xs sm:text-sm">
                                    <Rule ok={passwordChecks.length} label="Minimum 8 characters" />
                                    <Rule ok={passwordChecks.upper} label="One uppercase letter" />
                                    <Rule ok={passwordChecks.lower} label="One lowercase letter" />
                                    <Rule ok={passwordChecks.number} label="One number" />
                                    <Rule ok={passwordChecks.special} label="One special character" />
                                </div>
                            )}

                            {/* CONFIRM PASSWORD */}
                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    name="confirm_password"
                                    placeholder="Re-enter New Password"
                                    value={formData.confirm_password}
                                    onChange={handleChange}
                                    onFocus={() => setConfirmFocused(true)}
                                    required
                                    className={`w-full px-4 py-3 sm:py-3.5 bg-slate-800/50 border rounded-lg pr-12 text-sm sm:text-base text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formData.confirm_password && !isPasswordMatch
                                            ? "border-red-500"
                                            : "border-slate-700"
                                        }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword((v) => !v)}
                                    className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-slate-200 transition-colors"
                                >
                                    {showConfirmPassword ? (
                                        <Eye size={20} />
                                    ) : (
                                        <EyeOff size={20} />
                                    )}
                                </button>
                            </div>

                            {confirmFocused && formData.confirm_password && (
                                <div
                                    className={`text-xs sm:text-sm pl-1 ${isPasswordMatch ? "text-green-400" : "text-red-400"
                                        }`}
                                >
                                    {isPasswordMatch
                                        ? "✔ Passwords match"
                                        : "✖ Passwords do not match"}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading || !isPasswordValid || !isPasswordMatch}
                                className="w-full py-3 sm:py-3.5 rounded-lg font-medium text-white flex items-center justify-center gap-2 text-sm sm:text-base transition-colors bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading && (
                                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                                )}
                                {loading ? "Resetting..." : "Reset Password"}
                            </button>
                        </form>
                    </>
                ) : (
                    <div className="text-center space-y-3 sm:space-y-4">
                        <p className="text-green-400 font-semibold text-base sm:text-lg">
                            ✅ Password reset successful
                        </p>
                        <p className="text-sm sm:text-base text-slate-400">
                            Redirecting to login...
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ResetPassword;